import { Alert, Box } from '@mui/material';
import { Report, models } from 'powerbi-client';
import { EmbedProps } from 'powerbi-client-react';
import { useEffect, useState } from 'react';
import Loading from 'shared/components/Loading';
import { useI2PEnvironment } from 'shared/hooks/useI2PEnvironment';
import { EmbedIndividualAnalytic, useAvailableAnalyticsReports } from 'shared/individualAnalytics';
import { ApprovalDashboardData } from 'types/backend';

const APPROVAL_PAGE_DESKTOP_PAGE_NAME = 'ReportSectiona63a3043cd46308b8dd7';

type AnalyticDisplayAreaProps = {
    analyticsDrawerRow: ApprovalDashboardData | null;
};

export function AnalyticDisplayArea({ analyticsDrawerRow }: AnalyticDisplayAreaProps) {
    const { reportQuery } = useAvailableAnalyticsReports();
    const [report, setReport] = useState<Report>();
    const [isRendered, setIsRendered] = useState(false);
    const { isDev } = useI2PEnvironment();

    // For troubleshooting and getting more details of powerbi side
    useEffect(() => {
        if (isDev && report && isRendered) {
            console.log('get visuals');
            const getVisuals = async () => {
                const pages = await report?.getPages();
                if (!pages?.length) return;
                console.log('pages', pages);
                const activePage = pages.find((page) => page.isActive);
                if (!activePage) return;
                console.log('activePage', activePage);
                const pageFilters = await activePage.getFilters();
                console.log('pageFilters', pageFilters);
                const visuals = await activePage?.getVisuals();
                console.log('visuals', visuals);
                visuals.forEach(async (visual) => {
                    const visualFilters = await visual.getFilters();
                    console.log('visualFilters', visualFilters);
                });
            };
            getVisuals();
        }
    }, [report, isRendered, isDev]);

    if (!analyticsDrawerRow) return null;

    if (reportQuery.isLoading) {
        return (
            <Box flexGrow={1} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <Loading />
            </Box>
        );
    }

    if (!reportQuery?.data?.length) {
        return (
            <Alert severity='error'>
                No Active Reports
                <br />
                <br />
                Your login does not currently give you access to view any analytics reports. If you should have access
                to view analytics reports, submit a ticket through our application support portal. Otherwise, please
                close the analytics drawer.
            </Alert>
        );
    }

    const analyticsReport = reportQuery?.data?.find(
        (r) => r.reportName === 'Paragon Deviations, Sales Leaders & Performance Dashboard'
    );
    if (!analyticsReport) {
        return (
            <Alert severity='error'>
                No Active Report
                <br />
                <br />
                Your login does not currently give you access to view the analytics report. If you should have access to
                view the analytics report, submit a ticket through our application support portal. Otherwise, please
                close the analytics drawer.
            </Alert>
        );
    }

    const embedProps: (isMobile?: boolean) => EmbedProps = (isMobile = false) => ({
        embedConfig: {
            type: 'report',
            tokenType: models.TokenType.Embed,
            ...analyticsReport,
            pageName: APPROVAL_PAGE_DESKTOP_PAGE_NAME,
            filters: [
                {
                    $schema: 'http://powerbi.com/product/schema#basic',
                    target: {
                        table: 'CurrentPrices',
                        column: 'ItemId',
                    },
                    filterType: 1,
                    displaySettings: {
                        displayName: 'Product ID',
                    },
                    operator: 'In',
                    values: [analyticsDrawerRow.productId],
                    requireSingleSelection: false,
                },
                {
                    $schema: 'http://powerbi.com/product/schema#basic',
                    target: {
                        table: 'CurrentPrices',
                        column: 'PriceType',
                    },
                    filterType: 1,
                    operator: 'In',
                    values: [analyticsDrawerRow.priceType],
                    requireSingleSelection: false,
                },
            ],
            settings: {
                layoutType: isMobile ? models.LayoutType.MobilePortrait : models.LayoutType.Custom, // Custom
                customLayout: isMobile
                    ? undefined
                    : {
                          pageSize: {
                              type: 0, // widescreen
                          },
                          displayOption: 1, // fit to width
                      },
                panes: {
                    filters: {
                        visible: true,
                        expanded: false,
                    },
                    pageNavigation: {
                        visible: false,
                    },
                },
                background: 1, // transparent
            },
        },
        //Needed to access powerbi api's for seeing/changing config
        getEmbeddedComponent: (embeddedReport) => {
            // console.log('embeddedReport', embeddedReport);
            setReport(embeddedReport as Report);
        },
        eventHandlers: new Map([
            [
                'rendered',
                () => {
                    if (analyticsReport?.reportName && analyticsReport?.id) {
                        setIsRendered(true);
                        // trackViewPageMutation.mutateAsync({
                        //     selectedReport: analyticsReport?.reportName,
                        //     selectedReportId: analyticsReport?.id,
                        // });
                    }
                },
            ],
            [
                'visualClicked',
                function () {
                    // console.log('visualClicked', arguments);
                    setIsRendered((prev) => !prev);
                },
            ],
            [
                'loaded',
                () => {
                    const iframes = document.getElementsByTagName('iframe');
                    Array.from(iframes).forEach((iframe) => {
                        iframe.style.border = 'none';
                    });
                    // console.log('Report has loaded');
                    if (isRendered) {
                        setIsRendered(false);
                    }
                },
            ],
        ]),
    });

    return (
        <>
            <Box
                sx={{
                    display: { xs: 'none', sm: 'none', md: 'block' },
                    p: '1rem 2rem',
                    flexGrow: 1,
                    '& iframe': {
                        borderWidth: '0',
                        height: '100%',
                        width: '100%',
                    },
                }}
            >
                <EmbedIndividualAnalytic analyticsEmbedProps={embedProps()} height='100%' width='100%' />
            </Box>
            <Box
                sx={{
                    display: { xs: 'block', sm: 'block', md: 'none' },
                    p: '1rem 2rem',
                    flexGrow: 1,
                    '& iframe': {
                        borderWidth: '0',
                        height: '100%',
                        width: '100%',
                    },
                }}
            >
                <EmbedIndividualAnalytic analyticsEmbedProps={embedProps(true)} height='100%' width='100%' />
            </Box>
        </>
    );
}
